"use client";

import {
  PromoCodeCreateDialog,
  PromoCodeEditDialog,
  PromoCodesFilters,
  PromoCodesHeader,
  PromoCodesTable,
  PromoCodeViewDialog,
} from "@/components/promo-codes";
import { Skeleton } from "@/components/ui/skeleton";
import { PromoCode } from "@/core/entities/promo-code";
import {
  useConfig,
  useDeletePromoCode,
  usePromoCodes,
  useSetConfig,
} from "@/hooks";
import { useEffect, useMemo, useState } from "react";

export default function PromoCodesPage() {
  const { data: promoCodes, isLoading } = usePromoCodes();
  const { data: config, isLoading: isConfigLoading } = useConfig();
  const deletePromoCodeMutation = useDeletePromoCode();
  const setConfigMutation = useSetConfig();
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [productTypeFilter, setProductTypeFilter] = useState<string>("all");
  const [enablePromoCodesInForm, setEnablePromoCodesInForm] = useState(false);

  // Sync local state with config from database
  useEffect(() => {
    if (config) {
      setEnablePromoCodesInForm(config.promoCodesEnabled);
    } else if (!isConfigLoading && !config) {
      // Config doesn't exist yet, default to false
      setEnablePromoCodesInForm(false);
    }
  }, [config, isConfigLoading]);

  const handleEnablePromoCodesChange = async (enabled: boolean) => {
    setEnablePromoCodesInForm(enabled);
    try {
      await setConfigMutation.mutateAsync({
        promoCodesEnabled: enabled,
      });
    } catch (error) {
      // Revert on error
      setEnablePromoCodesInForm(!enabled);
      console.error("Failed to update config:", error);
    }
  };

  const filteredPromoCodes = useMemo(() => {
    const fetchedPromoCodes = promoCodes || [];

    return fetchedPromoCodes.filter((promoCode) => {
      const matchesSearch =
        promoCode.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promoCode.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = (() => {
        if (statusFilter === "all") return true;
        if (statusFilter === "active") {
          if (!promoCode.expirationDate) return true;
          return new Date(promoCode.expirationDate) >= new Date();
        }
        if (statusFilter === "expired") {
          if (!promoCode.expirationDate) return false;
          return new Date(promoCode.expirationDate) < new Date();
        }
        return true;
      })();

      const matchesProductType = (() => {
        if (productTypeFilter === "all") return true;
        return promoCode.enabledFor === productTypeFilter;
      })();

      return matchesSearch && matchesStatus && matchesProductType;
    });
  }, [promoCodes, searchTerm, statusFilter, productTypeFilter]);

  const handleView = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setViewDialogOpen(true);
  };

  const handleEdit = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setEditDialogOpen(true);
  };

  const handleDelete = (promoCodeId: string) => {
    deletePromoCodeMutation.mutate({ id: promoCodeId });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48 bg-blue-200" />
          <Skeleton className="h-10 w-32 bg-blue-200" />
        </div>

        <div className="flex gap-4">
          <Skeleton className="h-10 w-64 bg-blue-200" />
          <Skeleton className="h-10 w-32 bg-blue-200" />
        </div>

        <div className="space-y-4">
          <Skeleton className="h-12 w-full bg-blue-200" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={"sk" + i} className="h-16 w-full bg-blue-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <PromoCodesHeader
        onCreatePromoCode={() => setCreateDialogOpen(true)}
        promoCodesCount={filteredPromoCodes.length}
        enablePromoCodesInForm={enablePromoCodesInForm}
        onEnablePromoCodesInFormChange={handleEnablePromoCodesChange}
      />

      <PromoCodesFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        productTypeFilter={productTypeFilter}
        onProductTypeFilterChange={setProductTypeFilter}
      />

      <PromoCodesTable
        promoCodes={filteredPromoCodes}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PromoCodeViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        promoCode={selectedPromoCode}
      />

      <PromoCodeEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        promoCode={selectedPromoCode}
      />

      <PromoCodeCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
